import React from 'react';
import is from 'is_js';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import BaseSessionNav from 'components/BaseSessionNav';
import baseStyles from 'components/BaseSessionNav/style.postcss';
import styles from './style.postcss';
import { Button, connect } from 'omni-common-ui';
import { actions as Session } from 'data/SessionPlans';
import { actions as Group } from 'containers/Group';
import { actions as UiState } from 'data/UiState';
import { getNextUnit, getPreviousUnit, getUnitList } from 'data/Units/accessors';
import { accessors as RAv2CicAccessors } from 'data/RAv2Cic';

const DATE_FORMAT = 'YYYY-MM-DD';

class CoveredInClassRAv2SessionNav extends BaseSessionNav {
  _hasNext() {
    return this.props.currentSessionIndex < this.props.sessionCount - 1;
  }

  _hasPrevious() {
    return this.props.currentSessionIndex > 0;
  }

  _hasNextUnit() {
    return is.existy(this.props.nextUnitText);
  }

  _hasPreviousUnit() {
    return is.existy(this.props.previousUnitText);
  }

  _getNextUnitText() {
    return this.props.nextUnitText;
  }

  _getPreviousUnitText() {
    return this.props.previousUnitText;
  }

  _getHeadingText() {
    const { currentSession, levelNumber, sessionCount, currentUnit } = this.props;
    if (!currentSession) return '';
    const sessionText = is.existy(levelNumber) ? `Level ${levelNumber}` : `${currentSession.index}/${sessionCount}`;
    const unitText = currentUnit ? `, Story ${currentUnit.orderingSequence}` : '';
    return `${sessionText}${unitText}`;
  }

  _getAdditionalChildren() {
    const { currentSessionStartDate } = this.props;
    if (!currentSessionStartDate) return <div />;
    const date = currentSessionStartDate.format(DATE_FORMAT);
    const displayDate = currentSessionStartDate.format('ddd, D MMM YYYY');
    return <h2><time dateTime={date}>{displayDate}</time></h2>;
  }

  _renderPreviousUnit() {
    return null;
  }

  _renderNextUnit() {
    return null;
  }

  _renderCurrent() {
    return <div className={classnames(baseStyles.BaseSessionNav_current, {
      [baseStyles.__populated]: !!this.props.showCurrentNav,
    })}>
      {
        is.existy(this.props.currentLinkHref) ?
          <Button linkTo={this.props.currentLinkHref}>
            Current
          </Button> : null
      }
    </div>;
  }

  render() {
    if (!this.props.currentSession || !this.props.timeZone) {
      return null;
    }
    return <div className={styles.CoveredInClassRAv2SessionNav}>{super.render()}</div>;
  }
}

CoveredInClassRAv2SessionNav.propTypes = Object.assign({
  currentSession: PropTypes.object,
  currentSessionIndex: PropTypes.number,
  currentSessionStartDate: PropTypes.object,
  currentUnit: PropTypes.object,
  levelNumber: PropTypes.number,
  sessionCount: PropTypes.number,
  nextUnitText: PropTypes.string,
  previousUnitText: PropTypes.string,
  timeZone: PropTypes.string,
}, BaseSessionNav.propTypes);

function getSessionLinkHref(sessionPlan, buildRoute) {
  const sessionPlanId = sessionPlan && sessionPlan.id;
  const unitId = sessionPlanId &&
    Array.isArray(sessionPlan.unitIds) &&
    sessionPlan.unitIds[0];

  const params = is.existy(unitId) ?
    { unitId, sessionPlanId, mode: '' } :
    { unitId: 0, sessionPlanId, mode: '' };
  return sessionPlanId && buildRoute(params);
}

function getPreviousOrNextSession(sessionList, currentSessionIndex, previous) {
  return sessionList.get(previous ?
    currentSessionIndex - 1 : currentSessionIndex + 1);
}

function getUnitLinkHref(session, unitId, buildRoute) {
  return unitId && session && buildRoute({
    unitId,
    sessionPlanId: session.id,
    mode: '',
  });
}

function isSessionContainsUnit(session, unitId) {
  if (is.existy(unitId)) {
    session.unitIds.includes(unitId);
  }
  return false;
}

export function mapStateToProps(state, ownProps) {
  const { groupId, sessionPlanId: currentSessionId } = ownProps.params;
  if (! is.existy(groupId) || ! is.existy(currentSessionId)) {
    return;
  }
  const { buildRoute } = ownProps;
  const group = state.get('rootReducer').get('groups').get(groupId) &&
    state.get('rootReducer').get('groups').get(groupId).data;
  const sessions = Session.getSessions(state, groupId);
  const units = getUnitList(state, groupId);
  if (is.existy(group) && is.existy(sessions) && sessions.size !== 0 && is.existy(units)) {
    const { timeZone } = Group.getCentre(state, groupId);
    const sessionList = Session.getOrderSessions(state, groupId);
    const currentUnitId = is.existy(ownProps.params.unitId) ?
      ownProps.params.unitId :
      Session.getFirstUnitOfSession(state, currentSessionId);
    const defaultSession = Session.getDefaultSession(state, groupId);
    const currentSession = sessions.get(currentSessionId);
    const currentSessionIndex = currentSession.index - 1;
    const previousSession = getPreviousOrNextSession(sessionList, currentSessionIndex, true);
    const nextSession = getPreviousOrNextSession(sessionList, currentSessionIndex, false);
    const nextUnitId = currentUnitId > 0 ?
      getNextUnit(state, currentUnitId, groupId) && getNextUnit(state, currentUnitId, groupId).id :
      Session.getNextUnitIdBySessionId(state, groupId, currentSessionId);
    const previousUnitId = currentUnitId > 0 ?
      getPreviousUnit(state, currentUnitId, groupId) &&
          getPreviousUnit(state, currentUnitId, groupId).id :
      Session.getPreviousUnitIdBySessionId(state, groupId, currentSessionId);
    const nextUnitHref = isSessionContainsUnit(currentSession, nextUnitId) ?
        getUnitLinkHref(currentSession, nextUnitId, buildRoute) :
        getUnitLinkHref(
          Session.getFirstSessionOfUnit(state, groupId, nextUnitId),
          nextUnitId,
          buildRoute);
    const previousUnitHref = isSessionContainsUnit(currentSession, previousUnitId) ?
        getUnitLinkHref(currentSession, previousUnitId, buildRoute) :
        getUnitLinkHref(
          Session.getFirstSessionOfUnit(state, groupId, previousUnitId),
          previousUnitId,
          buildRoute);
    const nextUnit = units.get(nextUnitId);
    const previousUnit = units.get(previousUnitId);
    const nextUnitText = nextUnit && `U ${nextUnit.orderingSequence}`;
    const previousUnitText = previousUnit && `U ${previousUnit.orderingSequence}`;
    const shouldShowUnitNav = !! (units && units.size !== 0);
    const currentUnit = units.get(currentUnitId);
    const rav2Cic = RAv2CicAccessors.getRAv2Cic(state, currentSessionId);
    const levelNumber = rav2Cic ? rav2Cic.levelNumber : null;
    return {
      timeZone,
      currentLinkHref: getSessionLinkHref(defaultSession, buildRoute),
      currentSession,
      currentSessionIndex,
      currentSessionStartDate: currentSession.period.start.clone().tz(timeZone),
      currentUnit,
      levelNumber,
      nextSessionLinkHref: getSessionLinkHref(nextSession, buildRoute),
      nextUnitLinkHref: nextUnitHref,
      nextUnitText,
      previousSessionLinkHref: getSessionLinkHref(previousSession, buildRoute),
      previousUnitLinkHref: previousUnitHref,
      previousUnitText,
      showUnitNav: shouldShowUnitNav,
      showCurrentNav: true,
      sessionCount: sessions.size,
      isHide: ! UiState.getShowNavigationBar(state),
    };
  }
}

export default connect(mapStateToProps, null)(CoveredInClassRAv2SessionNav);



// WEBPACK FOOTER //
// ./components/CoveredInClassRAv2SessionNav/index.jsx